<template>
  <span
    class="search-switch"
    v-if="search_display_type === 'show'"
  >
    <a-button
      v-if="!isShowForm"
      type="link"
      size="small"
      @click="isShowForm = true"
    >
      <span class="expand-btn">{{ $t('展开') }}</span>
    </a-button>
    <div v-else>
      <a-button
        type="link"
        size="small"
        @click="isShowForm = false"
      >
        {{ $t('隐藏') }}
      </a-button>
      <a-button
        type="text"
        size="small"
        @click="searchConfigVisible = true"
        v-if="config?.sys_btn?.search_field_config !== false"
      >
        <a-tooltip :title="$t('设置搜索字段')">
          <SettingOutlined />
        </a-tooltip>
      </a-button>
    </div>
  </span>
  <search-config-modal
    v-model:open="searchConfigVisible"
    :search-config="searchConfig"
    :view-search-config="viewSearchConfig"
    @change="handleSearchConfigChange"
  />
  <search-summary
    v-if="!isShowForm && search_display_type === 'hide_and_show_summary'"
    :search-conditions="searchConditions"
    :search-config="searchConfig"
    :field-search-config="fieldSearchConfig"
    :event-bus="eventBus"
    :searchType="searchType"
    @update:searchConditions="handleUpdateSearchConditions"
    @change="handleSearchChange"
  />
  <div
    v-if="isShowForm"
    class="search-form-wraper"
    :style="{
      '--column-count': searchColumnCount || 0,
    }"
  >
    <SearchForm
      :searchConfig="searchConfig"
      :searchConditions="searchConditions"
      :viewSearchConfig="viewSearchConfig"
      :searchType="searchType"
      :searchColumnCount="searchColumnCount"
      :autoRefreshOnSearchChange="autoRefreshOnSearchChange"
      :eventBus="eventBus"
      :showQueryBtn="!searchColumnCount"
      :disabledShowSearchGroupName="disabledShowSearchGroupName"
      @update:searchConditions="handleUpdateSearchConditions"
      @change="handleSearchChange"
    />
  </div>
  <div
    class="bottom-content"
    v-if="searchColumnCount && isShowForm && !autoRefreshOnSearchChange"
  >
    <a-button
      size="small"
      type="primary"
      @click="handleRefresh"
    >
      查询
    </a-button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import searchSummary from './searchSummary.vue';
import SearchConfigModal from './tableConfig/searchConfig.vue';
import SearchForm from './SearchForm.vue';
import {
  SearchConditionItem,
  SearchConfig,
  SearchDisplayType,
  SearchType,
  ViewSearchItem,
} from './typing';
import { useTableProState } from './utils';

defineOptions({
  name: 'Search',
});

const props = defineProps({
  searchConfig: {
    type: Array as PropType<SearchConfig[]>,
    required: true,
  },
  /** 表头自定义搜索字段配置（query_field_search_config_url 查询结果），用于搜索摘要展示未在 search_config 中配置的字段 */
  fieldSearchConfig: {
    type: Array as PropType<SearchConfig[]>,
    default: () => [],
  },
  searchConditions: {
    type: Array as PropType<SearchConditionItem[]>,
    required: true,
  },
  eventBus: {
    type: Object as any,
    required: false,
  },
  searchType: {
    type: String as () => SearchType,
    default: 'advanced',
  },
  searchDisplayType: {
    type: String as () => SearchDisplayType,
    default: 'hide',
  },
  searchColumnCount: {
    type: Number,
    default: 0,
  },
  viewSearchConfig: {
    type: Object as PropType<Record<string, ViewSearchItem>>,
    default: () => ({}),
  },
  autoRefreshOnSearchChange: {
    type: Boolean,
    default: false,
  },
  disabledShowSearchGroupName: {
    type: Boolean,
    default: false, // 是否显示搜索分组名称，默认不显示
  },
});

const emit = defineEmits(['update:searchConditions', 'viewSearchConfigChange', 'change']);

const { t } = useI18n();
const isShowForm = ref(false);
const searchConfigVisible = ref(false);
const { config } = useTableProState();

const handleUpdateSearchConditions = (newConditions: any[]) => {
  emit('update:searchConditions', newConditions);
};

const handleSearchChange = () => {
  emit('change');
};

const handleRefresh = () => {
  props.eventBus.emit('refresh');
};

const handleSearchConfigChange = (data: any) => {
  emit('viewSearchConfigChange', data);
};

const search_display_type = computed(() => {
  return props.searchDisplayType || 'hide';
});

onMounted(() => {
  if (search_display_type.value === 'show_no_hide') {
    isShowForm.value = true;
  }
});
</script>

<style lang="less" scoped>
.search-form-wraper {
  padding-left: 2px;
  padding-right: 2px;
  max-height: 300px;
  overflow-y: auto;
}
.search-switch {
  display: inline-block;
  line-height: 32px;
  height: 32px;
}
.bottom-content {
  display: flex;
  justify-content: flex-end;
  padding: 8px 26px 8px 8px;
}
</style>
